import { Filters } from '@funkey/shared';
import { pipe } from 'effect';
import { createParser } from 'nuqs/server';
import z from 'zod';
import { decodeBase64UrlUtf8, encodeUtf8Base64Url } from './wishlist.utils';

export type Wishlist_SharedPayload = z.input<typeof Wishlist_SharedPayload.CODEC>;
export namespace Wishlist_SharedPayload {
    export type Item = z.infer<typeof COMPRESSED_ITEM_SCHEMA>;
    export type Data = z.output<typeof CODEC>;

    export const QUERY_PARAM_NAME = 'shared';
    export const QUERY_PARAM_PARSER = createParser<Wishlist_SharedPayload.Data>({
        parse(value) {
            try {
                const payload = Wishlist_SharedPayload.CODEC.safeDecode(
                    pipe(value, decodeBase64UrlUtf8, JSON.parse),
                );

                if (!payload.success) {
                    console.warn('Discarded invalid shared wishlist payload', payload.error);
                    return null;
                }

                return payload.data;
            } catch (error) {
                console.warn('Discarded invalid shared wishlist payload', error);
                return null;
            }
        },
        serialize(value) {
            return pipe(
                value,
                Wishlist_SharedPayload.CODEC.encode,
                JSON.stringify,
                encodeUtf8Base64Url,
            );
        },
    });

    export const ITEM_SCHEMA = z.object({
        activityId: z.number().int(),
        name: z.string(),
        slug: z.string(),
        imagePath: z.string().nullable(),
        variantId: z.number().int().nullable(),
        languageId: z.number().int().nullable(),
    });

    const PARTICIPANTS_SCHEMA = z.number().int().positive().nullable();

    export const COMPRESSED_ITEM_SCHEMA = z.object({
        a: ITEM_SCHEMA.shape.activityId,
        n: ITEM_SCHEMA.shape.name,
        s: ITEM_SCHEMA.shape.slug,
        i: ITEM_SCHEMA.shape.imagePath,
        v: ITEM_SCHEMA.shape.variantId,
        l: ITEM_SCHEMA.shape.languageId,
    });

    const PAYLOAD_SCHEMA = z.object({
        items: COMPRESSED_ITEM_SCHEMA.array(),
        participants: PARTICIPANTS_SCHEMA.default(null),
    });

    export const CODEC = z.codec(
        z.union([
            COMPRESSED_ITEM_SCHEMA.extend({ p: PARTICIPANTS_SCHEMA.optional() }).array(), // Legacy
            PAYLOAD_SCHEMA,
        ]),
        z.object({
            participants: z.number().int().positive().nullable(),
            items: ITEM_SCHEMA.array(),
        }),
        {
            decode(payload) {
                const parsed = Array.isArray(payload)
                    ? {
                          participants: pipe(
                              payload.map(x => x.p).filter(Filters.nonnull),
                              participants =>
                                  participants.length
                                      ? participants.reduce((acc, next) => acc + next, 0) /
                                        participants.length
                                      : 0,
                              Math.abs,
                              participants => participants || null,
                          ),
                          items: payload.map(({ p: _p, ...item }) => item),
                      }
                    : payload;

                return {
                    ...parsed,
                    items: parsed.items.map(item => ({
                        activityId: item.a,
                        name: item.n,
                        slug: item.s,
                        imagePath: item.i,
                        variantId: item.v,
                        languageId: item.l,
                    })),
                };
            },
            encode(value) {
                return {
                    participants: value.participants,
                    items: value.items.map(item => ({
                        a: item.activityId,
                        n: item.name,
                        s: item.slug,
                        i: item.imagePath,
                        v: item.variantId,
                        l: item.languageId,
                    })),
                };
            },
        },
    );
}
